import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardActionArea, CardActions, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Grid, IconButton, Paper, Snackbar, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon, ContentCopy as CopyIcon, OpenInNew as OpenIcon, RestaurantMenu as MenuIcon, Visibility as ViewsIcon,
} from '@mui/icons-material';
import MenuService from '../../services/menu.service';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import { apiError } from './menuUtils';

const MenuList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('menu.manage');

  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const show = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await MenuService.getMenus();
      setMenus(res.data?.data || []);
    } catch (error) {
      show(apiError(error, t('menus.errorLoading')), 'error');
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!name.trim()) return;
    try {
      setSaving(true);
      const res = await MenuService.createMenu({ name: name.trim() });
      navigate(`/menus/${res.data.data.uuid}`);
    } catch (error) {
      show(apiError(error, t('menus.errorSaving')), 'error');
    } finally {
      setSaving(false);
    }
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      show(t('menus.linkCopied'));
    } catch {
      show(url, 'info');
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" fontWeight="bold">{t('menus.title')}</Typography>
          <Typography color="text.secondary">{t('menus.subtitle')}</Typography>
        </Box>
        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setName(''); setDialog(true); }}>{t('menus.newMenu')}</Button>
        )}
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
      ) : menus.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
          <MenuIcon color="primary" sx={{ fontSize: 48 }} />
          <Typography variant="h6" fontWeight={700} mt={1}>{t('menus.emptyTitle')}</Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 440, mx: 'auto', mt: 0.5 }}>{t('menus.emptyHint')}</Typography>
          {canManage && (
            <Button variant="contained" startIcon={<AddIcon />} sx={{ mt: 3 }} onClick={() => { setName(''); setDialog(true); }}>{t('menus.newMenu')}</Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {menus.map((m) => (
            <Grid key={m.uuid} size={{ xs: 12, sm: 6, lg: 4 }}>
              <Card variant="outlined" sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardActionArea onClick={() => navigate(`/menus/${m.uuid}`)} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                  <Box sx={{ position: 'relative', height: 150, bgcolor: m.accent_color, color: '#fff', overflow: 'hidden' }}>
                    {m.cover_url && <Box component="img" src={m.cover_url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
                    {/* The menu's stripe, as diners see it */}
                    <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 14, display: 'flex' }}>
                      {Array.from({ length: 24 }).map((_, i) => (
                        <Box key={i} sx={{ flex: 1, bgcolor: i % 2 ? 'background.paper' : m.accent_color }} />
                      ))}
                    </Box>
                    {m.logo_url && (
                      <Box component="img" src={m.logo_url} alt="" sx={{ position: 'absolute', left: 16, top: 16, height: 44, maxWidth: 120, objectFit: 'contain', bgcolor: 'rgba(255,255,255,.9)', borderRadius: 1.5, p: 0.5 }} />
                    )}
                  </Box>
                  <Box p={2} flexGrow={1}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                      <Typography variant="h6" fontWeight={700} noWrap sx={{ flex: 1 }}>{m.name}</Typography>
                      {m.pending_orders_count > 0 && (
                        <Chip size="small" color="warning" label={t('menus.pendingOrdersCount', { count: m.pending_orders_count })} />
                      )}
                      <Chip size="small" color={m.published ? 'success' : 'default'} label={t(m.published ? 'menus.published' : 'menus.draft')} />
                    </Stack>
                    {m.tagline && <Typography variant="body2" color="text.secondary" noWrap>{m.tagline}</Typography>}
                    <Stack direction="row" spacing={2} mt={1.5} color="text.secondary">
                      <Typography variant="body2">{t('menus.dishCount', { count: m.items_count || 0 })}</Typography>
                      <Typography variant="body2">{t('menus.sectionCount', { count: m.sections_count || 0 })}</Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <ViewsIcon sx={{ fontSize: 16 }} />
                        <Typography variant="body2">{m.view_count || 0}</Typography>
                      </Stack>
                    </Stack>
                  </Box>
                </CardActionArea>
                <CardActions sx={{ px: 2, justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>{m.public_url.replace(/^https?:\/\//, '')}</Typography>
                  <Tooltip title={t('menus.copyLink')}><IconButton size="small" onClick={() => copy(m.public_url)}><CopyIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title={t(m.published ? 'menus.openMenu' : 'menus.notPublishedYet')}>
                    <span>
                      <IconButton size="small" component="a" href={m.public_url} target="_blank" rel="noopener" disabled={!m.published}><OpenIcon fontSize="small" /></IconButton>
                    </span>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('menus.newMenu')}</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label={t('menus.restaurantName')} value={name} sx={{ mt: 1 }}
            inputProps={{ maxLength: 120 }} helperText={t('menus.restaurantNameHint')}
            onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') create(); }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={create} disabled={saving || !name.trim()}>{t('menus.create')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default MenuList;
